import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ImageIcon,
  InstagramIcon,
  LinkIcon,
  PinterestIcon,
  PlayIcon,
  SpinnerIcon,
  TikTokIcon,
  YouTubeIcon,
} from "@/components/icons";
import type {
  IdeaResource,
  IdeaResourceEmbedProvider,
  IdeaResourceLocalMediaItem,
} from "@/domains/Ideas/types";
import { InlineMediaPlayer } from "./InlineMediaPlayer";

interface PrimaryResourceEmbedProps {
  resources: IdeaResource[];
}

const PROVIDER_BADGES: Record<
  IdeaResourceEmbedProvider,
  { label: string; Icon: React.FC<{ className?: string }>; brandClass: string }
> = {
  instagram: { label: "Instagram", Icon: InstagramIcon, brandClass: "text-pink-600" },
  pinterest: { label: "Pinterest", Icon: PinterestIcon, brandClass: "text-red-600" },
  tiktok: { label: "TikTok", Icon: TikTokIcon, brandClass: "text-gray-900" },
  youtube: { label: "YouTube", Icon: YouTubeIcon, brandClass: "text-red-600" },
  generic: { label: "Link", Icon: LinkIcon, brandClass: "text-gray-500" },
};

const SCRIPT_URLS: Record<string, string> = {
  instagram: "https://www.instagram.com/embed.js",
  tiktok: "https://www.tiktok.com/embed.js",
  pinterest: "https://assets.pinterest.com/js/pinit.js",
};

function hostFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export const PrimaryResourceEmbed: React.FC<PrimaryResourceEmbedProps> = ({
  resources,
}) => {
  const renderable = useMemo(
    () => resources.filter(canRender),
    [resources]
  );

  if (renderable.length === 0) return null;

  return (
    <div className="space-y-4">
      {renderable.map((resource, idx) => (
        <SingleResourceEmbed key={`${resource.url}-${idx}`} resource={resource} />
      ))}
    </div>
  );
};

const SingleResourceEmbed: React.FC<{ resource: IdeaResource }> = ({
  resource,
}) => {
  // Highest priority: locally-hosted media downloaded via Apify. Real
  // inline playback for Meta reels (which Meta's embed widget refuses to
  // play) plus consistent UX across reels/carousels/image posts.
  const localItems = playableLocalMedia(resource);
  if (localItems.length > 0) {
    return <InlineMediaPlayer items={localItems} />;
  }

  // Path B: Instagram. Synthesize the blockquote ourselves so the live
  // widget renders today, without waiting on Meta App Review. embed.js
  // fetches the post content client-side from a public Instagram URL,
  // no app token required.
  const instagramKind = detectInstagramKind(resource.url);
  if (instagramKind) {
    return (
      <ScriptEmbed
        provider="instagram"
        html={buildInstagramBlockquote(resource.url)}
        url={resource.url}
        title={resource.embed?.title}
        author={resource.embed?.authorName}
        badge={PROVIDER_BADGES.instagram}
        isVideoLike={instagramKind === "reel"}
      />
    );
  }

  // Path B: TikTok with a canonical /video/ID URL. Short links
  // (vm.tiktok.com) lack a data-video-id so they fall through to Path A.
  const tiktokId = detectTikTokVideoId(resource.url);
  if (tiktokId) {
    return (
      <ScriptEmbed
        provider="tiktok"
        html={buildTikTokBlockquote(resource.url, tiktokId)}
        url={resource.url}
        title={resource.embed?.title}
        author={resource.embed?.authorName}
        badge={PROVIDER_BADGES.tiktok}
        isVideoLike
      />
    );
  }

  // Path A: everything else relies on cached oEmbed metadata.
  if (!resource.embed) return null;

  const { embed } = resource;
  const provider = PROVIDER_BADGES[embed.provider] ?? PROVIDER_BADGES.generic;
  const isVideoLike =
    embed.embedType === "video" || embed.embedType === "reel";

  if (embed.status === "ok" && embed.provider === "youtube" && embed.html) {
    return (
      <YouTubeEmbed
        html={embed.html}
        url={resource.url}
        title={embed.title}
        author={embed.authorName}
        provider={provider}
      />
    );
  }

  if (
    embed.status === "ok" &&
    embed.html &&
    embed.provider === "pinterest"
  ) {
    return (
      <ScriptEmbed
        provider="pinterest"
        html={embed.html}
        url={resource.url}
        title={embed.title}
        author={embed.authorName}
        badge={provider}
        isVideoLike={false}
      />
    );
  }

  return (
    <OgHeroCard
      url={resource.url}
      thumbnailUrl={embed.thumbnailUrl}
      title={embed.title}
      author={embed.authorName}
      badge={provider}
      isVideoLike={isVideoLike}
    />
  );
};

// ---------------------------------------------------------------------------
// URL-level detection and blockquote synthesis (Path B)
// ---------------------------------------------------------------------------

function canRender(r: IdeaResource): boolean {
  if (playableLocalMedia(r).length > 0) return true;
  if (detectInstagramKind(r.url) || detectTikTokVideoId(r.url)) return true;
  return !!r.embed && r.embed.status !== "failed";
}

function playableLocalMedia(r: IdeaResource): IdeaResourceLocalMediaItem[] {
  const items = r.embed?.localMedia;
  if (!Array.isArray(items)) return [];
  return items.filter(
    (item) => item && typeof item.url === "string" && item.url.length > 0
  ) as IdeaResourceLocalMediaItem[];
}

function detectInstagramKind(url: string): "reel" | "post" | null {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (!host.split(".").includes("instagram")) return null;
    return /\/reels?\//i.test(url) ? "reel" : "post";
  } catch {
    return null;
  }
}

function detectTikTokVideoId(url: string): string | null {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (!host.split(".").includes("tiktok")) return null;
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

function buildInstagramBlockquote(url: string): string {
  // Strip query params: Meta's embed.js treats `?igsh=...` permalinks as
  // distinct from the canonical post URL and sometimes refuses to render.
  const canonical = url.split("?")[0];
  return (
    `<blockquote class="instagram-media" ` +
    `data-instgrm-permalink="${canonical}" ` +
    `data-instgrm-version="14" ` +
    `style="background:#FFF;border:0;border-radius:3px;margin:0;max-width:540px;min-width:326px;padding:0;width:99.375%;">` +
    `<a href="${canonical}"></a>` +
    `</blockquote>`
  );
}

function buildTikTokBlockquote(url: string, videoId: string): string {
  const canonical = url.split("?")[0];
  return (
    `<blockquote class="tiktok-embed" ` +
    `cite="${canonical}" ` +
    `data-video-id="${videoId}" ` +
    `style="max-width:605px;min-width:325px;">` +
    `<section></section>` +
    `</blockquote>`
  );
}

// ---------------------------------------------------------------------------
// YouTube — iframe with muted autoplay
// ---------------------------------------------------------------------------

const YouTubeEmbed: React.FC<{
  html: string;
  url: string;
  title?: string | null;
  author?: string | null;
  provider: { label: string; Icon: React.FC<{ className?: string }>; brandClass: string };
}> = ({ html, url, title, author, provider }) => {
  const enhanced = useMemo(() => addAutoplayToIframe(html), [html]);
  return (
    <EmbedFrame
      provider={provider}
      title={title}
      author={author}
      url={url}
      sizing="aspect-video"
    >
      <div
        className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0"
        dangerouslySetInnerHTML={{ __html: enhanced }}
      />
    </EmbedFrame>
  );
};

function addAutoplayToIframe(html: string): string {
  return html.replace(
    /<iframe([^>]*?)src="([^"]+)"/i,
    (_, pre, src) => {
      const separator = src.includes("?") ? "&" : "?";
      const augmented = `${src}${separator}autoplay=1&mute=1`;
      return `<iframe${pre}src="${augmented}" allow="autoplay; encrypted-media; picture-in-picture"`;
    }
  );
}

// ---------------------------------------------------------------------------
// Instagram / TikTok / Pinterest — official blockquote embed + script loader
// ---------------------------------------------------------------------------

const ScriptEmbed: React.FC<{
  provider: "instagram" | "tiktok" | "pinterest";
  html: string;
  url: string;
  title?: string | null;
  author?: string | null;
  badge: { label: string; Icon: React.FC<{ className?: string }>; brandClass: string };
  isVideoLike: boolean;
}> = ({ provider, html, url, title, author, badge, isVideoLike }) => {
  const ref = useRef<HTMLDivElement>(null);
  const rendered = useEmbedRenderedSignal(ref);
  useEmbedScript(provider, ref);

  // Reels and TikTok videos sit at 9:16 locked. Image posts and pins
  // self-size to the platform widget's natural height — we only reserve
  // a min-height while the script is still loading so the spinner has
  // room. Once the widget has rendered, drop the min-height and let the
  // container shrink to the widget's actual size (no blank space below
  // when the pin / post is shorter than our placeholder).
  // Reels and TikTok videos hit a hard 9:16 iPhone-SE cap so they
  // don't dominate the modal. Images and pins flow at their natural
  // height — the modal itself scrolls, no inner scrollbar.
  const sizingClass = isVideoLike
    ? "aspect-[9/16] max-h-[667px]"
    : `${rendered ? "min-h-0" : "min-h-[400px]"}`;

  return (
    <EmbedFrame
      provider={badge}
      title={title}
      author={author}
      url={url}
      sizing={sizingClass}
    >
      <div
        ref={ref}
        className="flex justify-center items-start p-2 bg-gray-50 w-full"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {!rendered && <LoadingOverlay />}
    </EmbedFrame>
  );
};

const LoadingOverlay: React.FC = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-gray-50 pointer-events-none">
    <SpinnerIcon className="w-6 h-6 text-gray-400 animate-spin" />
  </div>
);

// Detect when the platform script has finished injecting the embed
// (Instagram/TikTok replace the <blockquote> with an <iframe>; Pinterest
// builds a <span class="embedPin"> with inline content). MutationObserver
// is more reliable than script.onload because the script can be cached
// and still take a beat to process the blockquote in the DOM.
function useEmbedRenderedSignal(
  ref: React.RefObject<HTMLDivElement | null>
): boolean {
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const isRendered = () =>
      !!el.querySelector("iframe") ||
      !!el.querySelector("[data-pin-build]") ||
      !!el.querySelector(".embedPin");

    if (isRendered()) {
      setRendered(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (isRendered()) {
        setRendered(true);
        observer.disconnect();
      }
    });
    observer.observe(el, { childList: true, subtree: true });

    // Safety net: if the script never fires (offline, blocked), drop the
    // spinner after 8s so the user isn't staring at it forever.
    const timeout = setTimeout(() => setRendered(true), 8000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [ref]);

  return rendered;
}

function useEmbedScript(
  provider: "instagram" | "tiktok" | "pinterest",
  containerRef: React.RefObject<HTMLDivElement | null>
) {
  useEffect(() => {
    const src = SCRIPT_URLS[provider];
    if (!src) return;

    const reprocess = () => {
      if (provider === "instagram") {
        const w = window as unknown as {
          instgrm?: { Embeds: { process: () => void } };
        };
        w.instgrm?.Embeds.process();
      }
      if (provider === "pinterest") {
        const w = window as unknown as {
          PinUtils?: { build: (el?: Element) => void };
        };
        if (w.PinUtils && containerRef.current) {
          w.PinUtils.build(containerRef.current);
        }
      }
    };

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      reprocess();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = reprocess;
    document.body.appendChild(script);
  }, [provider, containerRef]);
}

// ---------------------------------------------------------------------------
// OG-fallback hero — large thumbnail + title + author
// ---------------------------------------------------------------------------

const OgHeroCard: React.FC<{
  url: string;
  thumbnailUrl?: string | null;
  title?: string | null;
  author?: string | null;
  badge: { label: string; Icon: React.FC<{ className?: string }>; brandClass: string };
  isVideoLike: boolean;
}> = ({ url, thumbnailUrl, title, author, badge, isVideoLike }) => {
  const [errored, setErrored] = useState(false);
  const hasImage = !!thumbnailUrl && !errored;

  return (
    <EmbedFrame
      provider={badge}
      title={title}
      author={author}
      url={url}
      sizing={isVideoLike ? "aspect-video" : "aspect-[4/5] max-h-[600px]"}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="relative flex items-center justify-center w-full h-full group bg-gray-900"
      >
        {hasImage ? (
          <img
            src={thumbnailUrl!}
            alt={title || ""}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer-when-downgrade"
            onError={() => setErrored(true)}
            className="max-w-full max-h-full w-auto h-auto object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <ImageIcon className="w-10 h-10 text-gray-400" />
          </div>
        )}
        {isVideoLike && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition">
            <span className="w-14 h-14 rounded-full bg-black/60 flex items-center justify-center opacity-90 group-hover:opacity-100 transition">
              <PlayIcon className="w-6 h-6 text-white translate-x-0.5" />
            </span>
          </span>
        )}
      </a>
    </EmbedFrame>
  );
};

// ---------------------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------------------

const EmbedFrame: React.FC<{
  provider: { label: string; Icon: React.FC<{ className?: string }>; brandClass: string };
  title?: string | null;
  author?: string | null;
  url: string;
  sizing: string;
  children: React.ReactNode;
}> = ({ provider, title, author, url, sizing, children }) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden mx-auto w-full max-w-[375px]">
      <div className={`relative w-full ${sizing} bg-black`}>{children}</div>
      <div className="px-3 py-2 flex items-center gap-2 text-xs">
        <span
          className={`inline-flex items-center gap-1 font-medium uppercase tracking-wide ${provider.brandClass}`}
        >
          <provider.Icon className="w-3.5 h-3.5" />
          {provider.label}
        </span>
        <span className="text-gray-300">·</span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-0 truncate text-gray-700 hover:text-gray-900"
        >
          {title || hostFromUrl(url)}
        </a>
        {author && (
          <span className="text-gray-400 truncate max-w-[40%]">
            {author}
          </span>
        )}
      </div>
    </div>
  );
};
